/** Credit history: paginated ledger (time, activity, project, actor, credits, balance) with filters and CSV export, and pack purchases */

import { useId, useState } from 'react'
import { Download } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { useT, type TranslateFn } from '../../i18n'
import { useProjects } from '../../hooks/use-projects'
import {
  CREDIT_TRANSACTION_TYPES,
  downloadCreditHistoryCsv,
  useCreditPurchases,
  useCreditTransactions,
  type CreditHistoryFilters,
  type CreditTransaction,
  type CreditTransactionType,
} from '../../hooks/use-credits'
import { buttonSecondaryClass, cardClass, errorTextClass, inputClass, labelClass } from '../ui/styles'
import { cn } from '../../lib/utils'
import {
  dateRangeToMs,
  describeTransaction,
  flattenPages,
  formatPrice,
  formatSignedCredits,
  transactionActor,
  transactionTypeKey,
} from './credit-format'

/** Shown for a column a row has no value for (for example rows written before attribution existed) */
const EMPTY_CELL = '—'
const thClass = 'px-4 py-2 font-medium'
const tdClass = 'px-4 py-3'

function transactionLabel(tx: CreditTransaction, t: TranslateFn): string {
  const desc = describeTransaction(tx.description)
  if (desc.kind === 'action') return desc.refund ? t('billing.history.refundOf', { action: t(desc.key) }) : t(desc.key)
  if (desc.kind === 'text') return desc.text
  return t('billing.history.noDescription')
}

function ListFooter({
  shown,
  total,
  hasMore,
  isFetching,
  onMore,
}: {
  shown: number
  total: number
  hasMore: boolean
  isFetching: boolean
  onMore: () => void
}) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const fmt = new Intl.NumberFormat(locale)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
      <p className="text-xs text-fg-subtle">{t('billing.history.showing', { shown: fmt.format(shown), total: fmt.format(total) })}</p>
      {hasMore && (
        <button type="button" onClick={onMore} disabled={isFetching} className={buttonSecondaryClass}>
          {isFetching ? t('common.loading') : t('billing.history.loadMore')}
        </button>
      )}
    </div>
  )
}

function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()
  return (
    <div className={cn(errorTextClass, 'm-4')} role="alert">
      {message}{' '}
      <button type="button" onClick={onRetry} className="cursor-pointer font-medium underline underline-offset-2">
        {t('common.retry')}
      </button>
    </div>
  )
}

function ActorCell({ tx }: { tx: CreditTransaction }) {
  const t = useT()
  const actor = transactionActor(tx)
  if (actor.kind === 'apiKey') {
    return <>{actor.name ? t('billing.history.actorApiKey', { name: actor.name }) : t('billing.history.actorApiKeyUnnamed')}</>
  }
  if (actor.kind === 'user') return <span title={actor.email ?? undefined}>{actor.name}</span>
  return <>{EMPTY_CELL}</>
}

function ProjectCell({ tx }: { tx: CreditTransaction }) {
  const t = useT()
  if (!tx.projectId) return <>{EMPTY_CELL}</>
  return tx.projectName ? <>{tx.projectName}</> : <span className="italic">{t('billing.history.projectDeleted')}</span>
}

/** The workspace credit ledger; used on /billing and in Settings › Credit history */
export function TransactionHistory() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const idBase = useId()
  const [type, setType] = useState<CreditTransactionType | ''>('')
  const [projectId, setProjectId] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const { data: projects } = useProjects()

  const filters: CreditHistoryFilters = { type: type || undefined, projectId: projectId || undefined, ...dateRangeToMs(fromDate, toDate) }
  const isFiltered = !!(type || projectId || fromDate || toDate)
  const query = useCreditTransactions(filters)
  const { items, total } = flattenPages(query.data)
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' })
  const numberFormat = new Intl.NumberFormat(locale)

  const exportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      await downloadCreditHistoryCsv(filters)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err))
    } finally {
      setExporting(false)
    }
  }

  const clearFilters = () => {
    setType('')
    setProjectId('')
    setFromDate('')
    setToDate('')
  }

  return (
    <div className={cardClass}>
      <div className="space-y-3 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-fg">{t('billing.history.transactions')}</h3>
          <button
            type="button"
            onClick={() => void exportCsv()}
            disabled={exporting}
            className={buttonSecondaryClass}
            title={t('billing.history.exportHint')}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {exporting ? t('billing.history.exporting') : t('billing.history.exportCsv')}
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <label htmlFor={`${idBase}-type`} className={labelClass}>
              {t('billing.history.filterLabel')}
            </label>
            <select
              id={`${idBase}-type`}
              value={type}
              onChange={(e) => setType(e.target.value as CreditTransactionType | '')}
              className={inputClass}
            >
              <option value="">{t('billing.history.filterAll')}</option>
              {CREDIT_TRANSACTION_TYPES.map((value) => {
                const key = transactionTypeKey(value)
                return (
                  <option key={value} value={value}>
                    {key ? t(key) : value}
                  </option>
                )
              })}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor={`${idBase}-project`} className={labelClass}>
              {t('billing.history.filterProject')}
            </label>
            <select id={`${idBase}-project`} value={projectId} onChange={(e) => setProjectId(e.target.value)} className={inputClass}>
              <option value="">{t('billing.history.filterAllProjects')}</option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor={`${idBase}-from`} className={labelClass}>
              {t('billing.history.filterFrom')}
            </label>
            <input
              id={`${idBase}-from`}
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={`${idBase}-to`} className={labelClass}>
              {t('billing.history.filterTo')}
            </label>
            <input
              id={`${idBase}-to`}
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        {isFiltered && (
          <button type="button" onClick={clearFilters} className="cursor-pointer text-xs font-medium text-accent-text underline underline-offset-2">
            {t('billing.history.clearFilters')}
          </button>
        )}
        {exportError && (
          <p className={errorTextClass} role="alert">
            {t('billing.history.exportFailed', { message: exportError })}
          </p>
        )}
      </div>

      {query.isLoading ? (
        <p className="px-4 py-8 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : query.isError && items.length === 0 ? (
        <ErrorRow message={t('billing.history.error')} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-fg-subtle">
          {isFiltered ? t('billing.history.emptyFiltered') : t('billing.history.empty')}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table aria-label={t('billing.history.transactions')} className="w-full min-w-[44rem] text-left text-sm">
              <thead className="text-xs text-fg-subtle">
                <tr className="border-b border-border">
                  <th scope="col" className={thClass}>
                    {t('billing.history.col.time')}
                  </th>
                  <th scope="col" className={thClass}>
                    {t('billing.history.col.activity')}
                  </th>
                  <th scope="col" className={thClass}>
                    {t('billing.history.col.project')}
                  </th>
                  <th scope="col" className={thClass}>
                    {t('billing.history.col.actor')}
                  </th>
                  <th scope="col" className={cn(thClass, 'text-right')}>
                    {t('billing.history.col.credits')}
                  </th>
                  <th scope="col" className={cn(thClass, 'text-right')}>
                    {t('billing.history.col.balance')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((tx) => {
                  const typeKey = transactionTypeKey(tx.type)
                  return (
                    <tr key={tx.id} className="align-top">
                      <td className={cn(tdClass, 'whitespace-nowrap text-fg-muted')}>
                        <time dateTime={tx.createdAt}>{dateFormat.format(new Date(tx.createdAt))}</time>
                      </td>
                      <td className={tdClass}>
                        <p className="break-words font-medium text-fg">{transactionLabel(tx, t)}</p>
                        <p className="mt-0.5 text-xs text-fg-subtle">{typeKey ? t(typeKey) : tx.type}</p>
                      </td>
                      <td className={cn(tdClass, 'text-fg-muted')}>
                        <ProjectCell tx={tx} />
                      </td>
                      <td className={cn(tdClass, 'break-words text-fg-muted')}>
                        <ActorCell tx={tx} />
                      </td>
                      <td className={cn(tdClass, 'text-right font-semibold tabular-nums', tx.amount > 0 ? 'text-success' : 'text-fg')}>
                        {formatSignedCredits(tx.amount, locale)}
                      </td>
                      <td className={cn(tdClass, 'text-right tabular-nums text-fg-muted')}>
                        {tx.balanceAfter >= 0 ? numberFormat.format(tx.balanceAfter) : EMPTY_CELL}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {query.isError && <ErrorRow message={t('billing.history.error')} onRetry={() => void query.fetchNextPage()} />}
          <ListFooter
            shown={items.length}
            total={total}
            hasMore={query.hasNextPage}
            isFetching={query.isFetchingNextPage}
            onMore={() => void query.fetchNextPage()}
          />
        </>
      )}
    </div>
  )
}

export function PurchaseHistory() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const query = useCreditPurchases()
  const { items, total } = flattenPages(query.data)
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
  const numberFormat = new Intl.NumberFormat(locale)

  return (
    <div className={cardClass}>
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-fg">{t('billing.history.purchases')}</h3>
      </div>
      {query.isLoading ? (
        <p className="px-4 py-8 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : query.isError && items.length === 0 ? (
        <ErrorRow message={t('billing.purchases.error')} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-fg-subtle">{t('billing.purchases.empty')}</p>
      ) : (
        <>
          <ul aria-label={t('billing.history.purchases')} className="divide-y divide-border">
            {items.map((p) => (
              <li key={p.id} className="flex items-start justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <p className="break-words text-sm font-medium text-fg">{p.packName}</p>
                  <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-fg-subtle">
                    <time dateTime={p.createdAt}>{dateFormat.format(new Date(p.createdAt))}</time>
                    <span className={p.status === 'refunded' ? 'text-warning' : undefined}>
                      {p.status === 'refunded'
                        ? t('billing.purchases.status.refunded')
                        : p.status === 'completed'
                          ? t('billing.purchases.status.completed')
                          : p.status}
                    </span>
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold text-success tabular-nums">
                    {t('billing.purchases.credits', { count: numberFormat.format(p.creditsGranted) })}
                  </p>
                  <p className="mt-0.5 text-xs text-fg-subtle tabular-nums">{formatPrice(p.amountPaid, p.currency, locale)}</p>
                </div>
              </li>
            ))}
          </ul>
          {query.isError && <ErrorRow message={t('billing.purchases.error')} onRetry={() => void query.fetchNextPage()} />}
          <ListFooter
            shown={items.length}
            total={total}
            hasMore={query.hasNextPage}
            isFetching={query.isFetchingNextPage}
            onMore={() => void query.fetchNextPage()}
          />
        </>
      )}
    </div>
  )
}
